import { SelectOption } from 'lib/components/types';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { HospitalService } from 'types';
import classes from './index.module.scss';

/**
 * Props for the ServiceSelect component
 */
interface Props {
	items: Array<HospitalService>; // List of available hospital services
	value?: number; // Currently selected service ID
	setValue: React.Dispatch<React.SetStateAction<number | undefined>>; // Callback to update selected service ID
	setLabel: React.Dispatch<React.SetStateAction<string | undefined>>; // Callback to update selected service name
	disabled?: boolean; // Whether the select is disabled
	selected: Array<number>; // IDs of services that are already selected (to be excluded)
}

/**
 * Dropdown component for selecting a hospital service
 * Filters out already selected services to prevent duplicates
 *
 * @param value - Currently selected service ID
 * @param setValue - Callback to update selected service ID
 * @param setLabel - Callback to update selected service name
 * @param disabled - Whether the select is disabled
 * @param items - List of available hospital services
 * @param selected - IDs of services that are already selected (to be excluded)
 */
const ServiceSelect = ({ value, setValue, setLabel, disabled, items, selected }: Props) => {
	// Available options for the dropdown
	const [options, setOptions] = useState<SelectOption<number>[]>([]);
	// Currently selected option
	const [option, setOption] = useState<SelectOption<number> | null>(null);

	/**
	 * Initialize options when items are loaded
	 * Transforms hospital services into the format required by react-select
	 */
	useEffect(() => {
		if (items.length === 0) return;

		const all: SelectOption<number>[] = items.map(({ id, service }) => ({
			value: id,
			label: service.name,
		}));

		setOptions(all);
	}, [items]);

	/**
	 * Filter out already selected services from the options
	 * Prevents selecting the same service multiple times
	 */
	useEffect(() => {
		if (selected.length === 0) return;
		const filtered: SelectOption<number>[] = items
			.filter((item) => !selected.includes(item.id))
			.map(({ id, service }) => ({
				value: id,
				label: service.name,
			}));

		setOptions(filtered);
	}, [selected]);

	/**
	 * Set the selected option when the value prop changes
	 * Finds the corresponding service and updates the local state
	 */
	useEffect(() => {
		if (value) {
			const item = items.find((item) => item.id === value)!;
			const option: SelectOption<number> = { label: item.service.name, value: item.id };
			setOption(option!);
		}
	}, [value]);

	/**
	 * Handle selection change in the dropdown
	 * Updates both local state and parent component state
	 *
	 * @param option - The newly selected option or null if cleared
	 */
	const handleChange = (option: SelectOption<number> | null) => {
		setOption(option);
		setValue(option?.value);
		setLabel(option?.label);
	};

	/**
	 * Render the react-select dropdown component
	 * Configured with custom styling and behavior options
	 */
	return (
		<Select
			autoFocus // Automatically focus the select when rendered
			isDisabled={disabled} // Disable the select if needed
			placeholder="Select Service"
			className={classes['react-select-container']}
			classNamePrefix="react-select"
			options={options}
			isClearable={true} // Allow clearing the selection
			isSearchable={true} // Allow searching by typing
			value={option}
			onChange={handleChange}
		/>
	);
};

export default ServiceSelect;
